import   React            ,
       { useState         ,
	     useEffect        ,
         useRef           ,
		 ChangeEvent      }  from "react";

import   classNames          from "classnames";

import   styles              from "./Field.module.scss";

import   Icon                from "../images/Icon";
import   ProgressBar      , 
       { DownloadProgress }  from "../gauge/ProgressBar";

export interface FieldImageProps {
  placeholder?   : string;
  value?         : string | number;
  mediaURL?      : string;
  readOnly?      : boolean;
  onChange?      : ( event: React.ChangeEvent<HTMLInputElement>, value: string | number | File ) => void;
  onDelete?      : () => void;
  onShowLibrary? : () => void;
  
  isLoading?     : boolean;
  loadProgress?  : DownloadProgress | null;
  width?         : string;
  height?        : string;
  glowColor?     : string;
  previewWidth?  : string;
  
  baseURL?       : string;
  extension?     : string;
  showPreview?   : boolean;
  
  
  
  library?       : boolean;
  upload?        : boolean;
}

export const FieldImage = ({ placeholder                   ,
                             value         = ""            ,
                             mediaURL                      ,
                             readOnly                      ,
                             onChange                      ,
                             onDelete                      , 
                             onShowLibrary                 ,
                             isLoading     = false         ,
                             loadProgress  = null          ,
                             width         = "100%"        ,
                             height        = "fit-content" ,
                             glowColor     = "var(--color-Field-Focus-normal)"    ,
                             previewWidth                  ,
                             baseURL       = ""            ,
                             extension     = ""            , 
                             showPreview   = true          ,
                             library       = false         ,
                             upload        = true          }: FieldImageProps) => {
								 
  const fieldStyle = { "--glow-color": glowColor } as React.CSSProperties;

  const [ mediaSrc   , setMediaSrc   ] = useState("");
  const [ inputValue , setInputValue ] = useState<string | number>(value);
  const [ isBlob     , setIsBlob     ] = useState(false);
  const [ imageError , setImageError ] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef   = useRef<string | null>(null);
  const fileInputId  = useRef(`file-input-${Math.random().toString(36).slice(2, 11)}`);

  // Nettoyage des URLs blob
  useEffect(() => {
	  
    return () => {
      if (blobUrlRef.current) 
	   {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
       }
    };
  }, []);


  useEffect(() => {

    if (mediaURL && !isBlob) {
      setMediaSrc(mediaURL);
    }
  }, [mediaURL, isBlob]);

  // Synchronisation avec la prop value
  useEffect(() => {
    if (value) {
      setInputValue(value);
      if (typeof value === "string") {
        const fullPath = constructFullPath(value, baseURL, extension);
        setMediaSrc(fullPath);
        setImageError(false);
        if (isBlob) {
          setIsBlob(false);
          if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
          }
        }
      }
    } else if (!isBlob) {
      setInputValue("");
      setMediaSrc("");
      setImageError(false);
    }
  }, [value, baseURL, extension]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
	  
    const file = event.target.files?.[0];
	
    if (!file) return;

    if (file.size === 0) {
      console.error("Le fichier est vide");
      return;
    }

    // Nettoyer l'ancienne URL blob si elle existe
    if (blobUrlRef.current) {
		
      URL.revokeObjectURL(blobUrlRef.current);
    }
	
    // Créer une nouvelle URL blob
    const objectURL = URL.createObjectURL(file);
    blobUrlRef.current = objectURL;

    setInputValue(file.name);
    setMediaSrc(objectURL);
    setIsBlob(true);
    setImageError(false);
    if (onChange) {
      onChange(event, file);
    }
  };

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
	  
    const input = event.target.value;
    setInputValue(input);
    setImageError(false);

    // Si l'utilisateur tape manuellement, on sort du mode blob
    if (isBlob) {
      setIsBlob(false);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    }

    // Construire le chemin complet
    if (input) {
      const fullPath = constructFullPath(input, baseURL, extension);
      setMediaSrc(fullPath);
    } else {
      setMediaSrc("");
    }
    if (onChange) {
      onChange(event, input);
    }
  };

  const handleUploadClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleLibraryClick = () => {
    if (readOnly) return;
    if (onShowLibrary) {
      onShowLibrary();
    }
  };

  const handleDeleteClick = () => {
    if (readOnly) return;

    // Révoquer l'ancienne URL blob si présente
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // Réinitialiser tous les états liés au média
    setMediaSrc("");
    setInputValue("");
    setIsBlob(false);
    setImageError(false);

    // Réinitialiser l'input file (pour permettre un nouvel upload du même fichier)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (onDelete) {
      onDelete();
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const constructFullPath = (value: string, baseURL: string, extension: string): string => {
    if (value.startsWith("blob:") || value.startsWith("http://") || value.startsWith("https://")) {
      return value;
    }
    let fullPath = value;
    if (!value.startsWith("/")) {
      fullPath = `${baseURL}${value}`;
    }
    if (!value.includes(".")) {
      fullPath += extension || ".webp";
    }
    return fullPath;
  };

  // Fonction renderPreview avec le composant ProgressBar
  const renderPreview = (): JSX.Element | null => {
const srcToDisplay = isBlob ? mediaSrc : (mediaURL || mediaSrc);
    
    if (!showPreview) return null;

    // Affichage de la progression pendant le chargement avec le composant ProgressBar
    if (isLoading && loadProgress) {
      return <ProgressBar progress={loadProgress} height={32} />;
    }

    // Pas de source disponible
    if (!srcToDisplay) return null;

    // Erreur de chargement
    if (imageError) {
      return (
        <div className={classNames(styles.message, styles.error)}>
          Impossible de charger l'image
        </div>
      );
    }

    // Affichage de l'image
    return (
      <div className={classNames(styles.image_preview)}>
        <img
          src={srcToDisplay}
          alt="Preview"
          className={classNames(styles.img)}
          style={{ width: previewWidth || "100%", height: "auto" }}
          onError={handleImageError}
        />
      </div>
    );
  };

  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      <div className={classNames(styles.file_input_wrapper)}>
        <div className={classNames(styles.field_with_icon)}>
          {/* Input file caché */}
          <input ref       = {fileInputRef}
                 type      = "file"
                 id        = {fileInputId.current}
                 className = {classNames(styles.file_input_hidden)}
                 accept    = "image/*"
                 onChange  = {handleFileChange}
                 disabled  = {readOnly}
          />
          {/* Input texte visible */}
          <input type        = "text"
                 className   = {classNames(styles.field_input)}
                 placeholder = {placeholder}
                 value       = {typeof inputValue === "string" ? inputValue : ""}
                 readOnly    = {readOnly}
                 onChange    = {handleTextChange}
          />
          &nbsp;
          {
           !readOnly &&
            upload &&
            <Icon onClick    = {handleUploadClick}
                  name       = "upload"
                  color      = "var(--color-Field-Icon-normal)"
                  hoverColor = "var(--color-Field-Icon-hover-normal)"
 				width={18}
                height={18}				  />
          }
          &nbsp;
          {
           !readOnly &&
            library &&
            <Icon onClick    = {handleLibraryClick}
                  name       = "library3"
                  color      = "var(--color-Field-Icon-normal)"
                  hoverColor = "var(--color-Field-Icon-hover-normal)"
 				width={18}
                height={18}				  />
          }
          &nbsp;
          {
           !readOnly &&
            onDelete &&
            <Icon onClick    = {handleDeleteClick}
                  name       = "trashCan"
                  color      = "var(--color-Field-Icon-normal)"
                  hoverColor = "var(--color-Field-Icon-hover-normal)" 
				width={18}
                height={18} />
          }
        </div>
        {/* Prévisualisation / Player */}
        {renderPreview()}
      </div>
    </div>
  );
};

export default FieldImage;